import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, ArrowLeft, BookOpen, Tag,
  GraduationCap, CheckCircle, Loader2, AlertCircle, Package,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { fetchProductById } from '../services/api';

const SYLLABUS_STYLE = {
  Local:     'bg-green-500/10 text-green-400 border-green-500/20',
  Cambridge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Edexcel:   'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [added,   setAdded]   = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchProductById(id)
      .then(res => setProduct(res.data))
      .catch(() => setError('Product not found or failed to load.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = () => {
    if (!product || product.stock === 0) return;
    addToCart({ ...product, id: product._id, price: String(product.price) });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-500">
        <Loader2 size={36} className="animate-spin text-accentBlue mb-4" />
        <p className="text-sm">Loading product…</p>
      </div>
    );
  }

  // ── Error ──
  if (error || !product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <AlertCircle size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Product Not Found</h2>
        <p className="text-gray-400 text-sm mb-6">{error}</p>
        <button onClick={() => navigate('/shop')}
          className="px-5 py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
          Back to Shop
        </button>
      </div>
    );
  }

  const isOut = product.stock === 0;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">

      {/* Back */}
      <button onClick={() => navigate('/shop')}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8 group transition-colors">
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        Back to Shop
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">

        {/* ── Image ── */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <div className="relative rounded-3xl overflow-hidden bg-white/5 border border-white/10 aspect-[3/4] flex items-center justify-center">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={product.title}
                className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-600">
                <BookOpen size={56} className="mb-3 opacity-30" />
                <span className="text-sm opacity-30">No Image Available</span>
              </div>
            )}

            {/* Syllabus badge overlay */}
            <div className="absolute top-4 left-4">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${SYLLABUS_STYLE[product.syllabus] || 'bg-white/10 text-gray-300 border-white/20'}`}>
                {product.syllabus}
              </span>
            </div>

            {isOut && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="px-4 py-2 bg-red-500 text-white text-sm font-bold rounded-full">
                  Out of Stock
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── Details ── */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col justify-center">

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-5">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${SYLLABUS_STYLE[product.syllabus] || 'bg-white/10 text-gray-300 border-white/20'}`}>
              {product.syllabus} Syllabus
            </span>
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-gray-300">
              {product.level}
            </span>
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-accentBlue/8 border border-accentBlue/20 text-accentBlue flex items-center gap-1.5">
              <Tag size={10} /> {product.subject}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-extrabold text-white leading-tight mb-4">{product.title}</h1>

          {/* Price */}
          <div className="flex items-end gap-3 mb-6">
            <span className="text-4xl font-extrabold text-accentBlue">
              Rs. {Number(product.price).toLocaleString()}
            </span>
          </div>

          {/* Description */}
          <div className="bg-white/4 border border-white/10 rounded-2xl p-5 mb-6">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BookOpen size={13} /> About this product
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">{product.description}</p>
          </div>

          {/* Info rows */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-3 text-sm">
              <GraduationCap size={16} className="text-accentBlue shrink-0" />
              <span className="text-gray-400">Syllabus:</span>
              <span className="text-white font-semibold">{product.syllabus}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Tag size={16} className="text-accentBlue shrink-0" />
              <span className="text-gray-400">Subject:</span>
              <span className="text-white font-semibold">{product.subject}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Package size={16} className={isOut ? 'text-red-400' : 'text-green-400'} />
              <span className="text-gray-400">Stock:</span>
              <span className={`font-semibold ${isOut ? 'text-red-400' : 'text-green-400'}`}>
                {isOut ? 'Out of Stock' : `${product.stock} available`}
              </span>
            </div>
            {!isOut && (
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle size={16} className="text-green-400 shrink-0" />
                <span className="text-green-400 font-medium">Ready to order</span>
              </div>
            )}
          </div>

          {/* Add to cart */}
          <button
            onClick={handleAddToCart}
            disabled={isOut}
            className={`w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-3 transition-all ${
              added
                ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                : isOut
                ? 'bg-white/5 border border-white/10 text-gray-600 cursor-not-allowed'
                : 'bg-accentBlue text-black hover:scale-[1.02] shadow-lg shadow-accentBlue/20'
            }`}
          >
            <ShoppingCart size={20} />
            {added ? 'Added to Cart!' : isOut ? 'Out of Stock' : 'Add to Cart'}
          </button>

          {!isOut && (
            <p className="text-center text-xs text-gray-500 mt-3">
              Secure checkout · Fast delivery
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ProductDetails;
